import React, { useEffect, useRef, useState } from "react";
import { Pagination } from "./Pagination";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { Activity, RefreshCw, Search, ChevronUp, ChevronDown } from "lucide-react";

interface ProcessListProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
    platform?: string;
}

const DEFAULT_PER_PAGE = 10;

const ProcessList: React.FC<ProcessListProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
    platform,
}) => {
    const isWindows = platform?.toLowerCase() === "windows";
    const [processes, setProcesses] = useState<any[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
    const [search, setSearch] = useState<string>("");
    const [searchInput, setSearchInput] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    const fetchProcesses = async (
        skip = 0,
        limit = perPage,
        searchTerm = search,
        currentSort = sortConfig
    ) => {
        setLoading(true);
        try {
            const queryObj: any = { client_id, organization_id, agent_id, asset_id };
            if (searchTerm) queryObj.$or = [
                { process_name: { $ilike: `%${searchTerm}%` } },
                { path: { $ilike: `%${searchTerm}%` } },
                { status: { $ilike: `%${searchTerm}%` } },
            ];

            const params: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj)
            };

            // Add API Sorting if requested
            if (currentSort) {
                params.sort_by = currentSort.key;
                params.sort_order = currentSort.direction;
            }

            const response = await baseapi.get("/asset_running_processes", { params });
            const rows = response.data?.data ?? [];
            setProcesses(rows);
            setTotal(response.data?.total ?? 0);

        } catch (err: any) {
            showError(err.message);
            setProcesses([]);
            setTotal(0);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchProcesses(0, perPage, "", sortConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client_id, organization_id, agent_id, asset_id]);

    // const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    //     if (e.key === "Enter") {
    //         const term = searchInput.trim();
    //         setSearch(term);
    //         setPage(1);
    //         fetchProcesses(0, perPage, term, sortConfig);
    //     }
    // };
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);

        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        debounceTimer.current = setTimeout(() => {
            const trimmed = value.trim();
            setSearch(trimmed);
            setPage(1);
            fetchProcesses(0, perPage, trimmed, sortConfig);
        }, 400);
    };

    // const handleSearchButton = () => {
    //     const term = searchInput.trim();
    //     setSearch(term);
    //     setPage(1);
    //     fetchProcesses(0, perPage, term, sortConfig);
    // };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= Math.ceil(total / perPage)) {
            setPage(newPage);
            fetchProcesses((perPage * (newPage - 1)), perPage, search, sortConfig);
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setPerPage(value);
        setPage(1);
        fetchProcesses(0, value, search, sortConfig);
    };

    const handleRefresh = () => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        setSearch("");
        setSearchInput("");
        setPage(1);
        fetchProcesses(0, perPage, "", sortConfig);
    };

    const handleSort = (key: string) => {
        let newDirection: "asc" | "desc" = "asc";
        if (sortConfig && sortConfig.key === key) {
            newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
        }
        const newSortConfig = { key, direction: newDirection };
        setSortConfig(newSortConfig);
        setPage(1); // Reset to page 1 on sort
        fetchProcesses(0, perPage, search, newSortConfig);
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, widthClass, textClass = "text-slate-700 dark:text-gray-200" }: { label: string; sortKey: string; widthClass?: string, textClass?: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className={`${widthClass} px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider border-b border-slate-200 dark:border-gray-700 cursor-pointer group select-none hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors ${textClass}`}
            >
                <div className="flex items-center gap-1">
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-blue-600 dark:text-blue-400" />
                            ) : (
                                <ChevronDown size={12} className="text-blue-600 dark:text-blue-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-slate-400 dark:text-gray-200 group-hover:text-slate-600 dark:group-hover:text-white" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    return (
        <div className="p-3">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-3 border-b border-slate-200 dark:border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <Activity className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">Running Processes</h2>
                        <p className="text-slate-500 dark:text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {total || '...'} processes found
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handleRefresh}
                        disabled={loading}
                        className="p-2 bg-white dark:bg-gray-800 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>

                    <div className="relative">
                        <Search className="w-3.5 h-3.5 text-slate-400 dark:text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            value={searchInput}
                            onChange={handleSearchChange}
                            placeholder="Search process..."
                            className="w-64 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors h-8 shadow-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-white dark:bg-gray-900/40 rounded-md border border-slate-200 dark:border-gray-700 overflow-hidden shadow-sm">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full table-fixed min-w-[1000px] overflow-x-auto custom-scroll">
                        <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr>
                                <SortableHeader widthClass={isWindows ? "w-[26%]" : "w-[18%]"} label="Process Name" sortKey="process_name" />
                                <SortableHeader widthClass={isWindows ? "w-[42%]" : "w-[28%]"} label="Path" sortKey="path" />
                                {!isWindows && <SortableHeader widthClass="w-[8%]" label="CPU %" sortKey="cpu_percent" textClass="text-amber-700 dark:text-orange-400" />}
                                {!isWindows && <SortableHeader widthClass="w-[8%]" label="Mem %" sortKey="memory_percent" textClass="text-purple-700 dark:text-purple-400" />}
                                <SortableHeader widthClass="w-[8%]" label="Mem MB" sortKey="memory_usage_mb" />
                                <SortableHeader widthClass="w-[6%]" label="PID" sortKey="pid" textClass="text-blue-600 dark:text-blue-400" />
                                {!isWindows && <SortableHeader widthClass="w-[6%]" label="Threads" sortKey="threads" />}
                                <SortableHeader widthClass="w-[8%]" label="Status" sortKey="status" />
                                <SortableHeader widthClass="w-[10%]" label="Start Time" sortKey="start_time" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                            {loading ? (
                                <tr>
                                    <td colSpan={isWindows ? 6 : 9} className="py-10 text-center text-slate-500 dark:text-gray-200 text-[11px]">Loading processes...</td>
                                </tr>
                            ) : processes.length === 0 ? (
                                <tr>
                                    <td colSpan={isWindows ? 6 : 9} className="py-10 text-center text-slate-500 dark:text-gray-200 text-[11px]">No processes found</td>
                                </tr>
                            ) : (
                                processes.map((proc, idx) => (
                                    <tr key={proc.id || idx} className="hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors duration-75">
                                        <td className="px-3 py-1.5 text-slate-900 dark:text-white text-[11px] font-bold truncate" title={proc.process_name}>{proc.process_name}</td>
                                        <td className="px-3 py-1.5 text-slate-600 dark:text-gray-200 text-[10px] truncate" title={proc.path}>{proc.path || "-"}</td>
                                        {!isWindows && <td className="px-3 py-1.5 text-amber-700 dark:text-orange-300 font-mono text-[10px] font-bold">{proc.cpu_percent?.toFixed(2) ?? "0.00"}</td>}
                                        {!isWindows && <td className="px-3 py-1.5 text-purple-700 dark:text-purple-300 font-mono text-[10px] font-bold">{proc.memory_percent?.toFixed(2) ?? "0.00"}</td>}
                                        <td className="px-3 py-1.5 text-slate-600 dark:text-gray-200 font-mono text-[10px]">{proc.memory_usage_mb?.toFixed(2) ?? "-"}</td>
                                        <td className="px-3 py-1.5 text-blue-600 dark:text-blue-400 font-mono text-[10px]">{proc.pid}</td>
                                        {!isWindows && <td className="px-3 py-1.5 text-slate-600 dark:text-gray-300 text-[10px]">{proc.threads}</td>}
                                        <td className="px-3 py-1.5">
                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase whitespace-nowrap ${proc.status === "Running"
                                                ? "text-emerald-700 dark:text-green-400 border-emerald-200 dark:border-green-500/20 bg-emerald-50 dark:bg-green-500/5"
                                                : "text-slate-600 dark:text-gray-200 border-slate-200 dark:border-gray-700 bg-slate-100 dark:bg-gray-800/20"
                                                }`}>
                                                {proc.status}
                                            </span>
                                        </td>
                                        <td className="px-3 py-1.5 text-slate-500 dark:text-gray-200 text-[9px] font-mono whitespace-nowrap">{proc.start_time || "-"}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot className="bg-slate-50/50 dark:bg-gray-900/60 sticky bottom-0">
                            <tr>
                                <td colSpan={isWindows ? 6 : 9} className="p-0 border-t border-slate-200 dark:border-gray-700">
                                    <Pagination
                                        currentPage={page}
                                        totalPages={Math.max(1, Math.ceil(total / perPage))}
                                        onPageChange={handlePageChange}
                                        itemsPerPage={perPage}
                                        totalItems={total}
                                        onItemsPerPageChange={handleItemsPerPageChange}
                                    />
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ProcessList;